"use client";
import { postData } from "@/api/api";
import { yupResolver } from "@hookform/resolvers/yup";
import { useState } from "react";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import ErrorMsg from "../common/error-msg";

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

const schema = yup.object().shape({
  name: yup.string().required().label("Name"),
  email: yup.string().required().email().label("Email"),
  subject: yup.string().required().min(5).label("Subject"),
  message: yup.string().required().label("Message"),
});

const ContactForm = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [toastMessage, setToastMessage] = useState<string>("");

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = handleSubmit(async (data) => {
    try {
      setLoading(true);
      const resp = await postData(`/contacts`, data);

      if (!resp?.success) {
        setToastMessage(resp?.message || "Something went wrong");
        return;
      }

      setToastMessage(resp?.message || "Message sent");
      reset();
    } catch (error) {
      console.log(error as string);
      setToastMessage((error as string) || "Submission failed");
    } finally {
      setLoading(false);
      setTimeout(() => setToastMessage(""), 4000);
    }
  });

  return (
    <>
      <div className="cf-wrapper">
        <h2 className="cf-title">Contact Us</h2>

        <form onSubmit={onSubmit} id="contact-form" className="cf-form">
          <div className="cf-row two-col">
            <div className="cf-field">
              <label className="cf-label">
                Name <span className="required">*</span>
              </label>
              <input id="name" {...register("name")} placeholder="Your name" type="text" className="cf-input" />
              <ErrorMsg msg={errors.name?.message!} />
            </div>

            <div className="cf-field">
              <label className="cf-label">
                Email <span className="required">*</span>
              </label>
              <input id="email" {...register("email")} placeholder="you@email.com" type="email" className="cf-input" />
              <ErrorMsg msg={errors.email?.message!} />
            </div>
          </div>

          <div className="cf-row">
            <div className="cf-field">
              <label className="cf-label">
                Subject <span className="required">*</span>
              </label>
              <input id="subject" {...register("subject")} placeholder="Subject" type="text" className="cf-input" />
              <ErrorMsg msg={errors.subject?.message!} />
            </div>
          </div>

          <div className="cf-row">
            <div className="cf-field">
              <label className="cf-label">
                Message <span className="required">*</span>
              </label>
              <textarea {...register("message")} cols={30} rows={7} placeholder="Write your message..." className="cf-textarea" />
              <ErrorMsg msg={errors.message?.message!} />
            </div>
          </div>

          {toastMessage && <div className="cf-toast">{toastMessage}</div>}

          <div className="cf-row">
            <div className="cf-field submit-wrap">
              <button type="submit" className={`cf-btn ${loading ? "loading" : ""}`} disabled={loading}>
                {loading ? "Sending..." : "Send Message"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* styled-jsx css */}
      <style jsx>{`
        :root {
          --lav-600: #6b5bff;
          --lav-700: #5a2dbf;
          --card-bg: #ffffff;
          --page-bg: #f6f3ff;
          --muted: #6b6b7a;
        }

        .cf-wrapper {
          max-width: 920px;
          margin: 32px auto;
          padding: 28px;
          border-radius: 18px;
          background: linear-gradient(180deg, rgba(255,255,255,0.9), rgba(246,243,255,0.6));
          box-shadow:
            18px 18px 50px rgba(110,80,220,0.12),
            -8px -8px 30px rgba(255,255,255,0.8),
            inset 1px 1px 0 rgba(255,255,255,0.6);
          border: 1px solid rgba(150,130,255,0.12);
          transform-style: preserve-3d;
        }

        .cf-title {
          text-align: center;
          font-size: 28px;
          margin: 6px 0 20px;
          color: var(--lav-700);
          text-shadow: 0 6px 18px rgba(90,60,180,0.10);
          font-weight: 700;
        }

        .cf-form {
          display: block;
        }

        .cf-row {
          margin-bottom: 18px;
        }

        .two-col {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 18px;
        }

        .cf-field {
          display: flex;
          flex-direction: column;
        }

        .cf-label {
          font-size: 14px;
          color: #2b2540;
          margin-bottom: 8px;
          font-weight: 600;
        }

        .required {
          color: #e14b4b;
          margin-left: 4px;
        }

        .cf-input,
        .cf-textarea {
          background: linear-gradient(180deg, #fff, #fbfaff);
          border: 1px solid rgba(160,135,255,0.18);
          padding: 12px 14px;
          border-radius: 12px;
          font-size: 15px;
          color: #231f3a;
          box-shadow:
            6px 6px 18px rgba(120,80,220,0.08),
            -3px -3px 8px rgba(255,255,255,0.9),
            inset 1px 1px 0 rgba(255,255,255,0.6);
          transition: all 0.18s ease;
          outline: none;
        }

        .cf-input:focus,
        .cf-textarea:focus {
          transform: translateY(-1px);
          box-shadow:
            8px 8px 22px rgba(100,70,220,0.12),
            -4px -4px 10px rgba(255,255,255,0.9),
            inset 1px 1px 0 rgba(255,255,255,0.6);
          border-color: var(--lav-600);
        }

        .cf-textarea {
          min-height: 140px;
          resize: vertical;
        }

        .cf-toast {
          background: #e6fff0;
          color: #0b6b2b;
          padding: 12px 14px;
          border-radius: 10px;
          margin: 6px 0 10px;
          text-align: center;
          font-weight: 600;
          box-shadow: 0 6px 18px rgba(50,180,100,0.06);
        }

        .submit-wrap {
          display: flex;
          justify-content: center;
        }

        .cf-btn {
          background: linear-gradient(135deg, var(--lav-600), var(--lav-700));
          color: #7F56D9;
          padding: 12px 28px;
          border-radius: 12px;
          border: none;
          font-weight: 700;
          font-size: 15px;
          cursor: pointer;
          box-shadow:
            0 10px 28px rgba(90,60,200,0.18),
            inset 0 2px 8px rgba(255,255,255,0.08);
          transition: transform 0.18s ease, box-shadow 0.18s ease, opacity 0.18s ease;
        }

        .cf-btn:hover {
          transform: translateY(-3px);
          box-shadow:
            0 16px 40px rgba(80,50,190,0.22),
            inset 0 2px 8px rgba(255,255,255,0.12);
        }

        .cf-btn.loading {
          opacity: 0.85;
          cursor: not-allowed;
        }

        /* small screens */
        @media (max-width: 768px) {
          .cf-wrapper {
            padding: 20px;
            margin: 20px;
          }

          .two-col {
            grid-template-columns: 1fr;
          }

          .cf-title {
            font-size: 22px;
          }

          .cf-textarea {
            min-height: 120px;
          }
        }

        /* very small screens */
        @media (max-width: 420px) {
          .cf-wrapper {
            padding: 16px;
            border-radius: 14px;
          }
          .cf-btn {
            width: 100%;
          }
        }
      `}</style>
    </>
  );
};

export default ContactForm;
